import { OverlayTrigger, Tooltip } from "react-bootstrap"
import { formatCurrency } from "src/shared/utils/formatCurrency"
import type { EngagementBudgetHeader } from "@models/engagementBudgetTree.model"

export function BudgetHeader({ header }: { header: EngagementBudgetHeader }) {
  const days = header.days_until_replenish
  const cycleValue =
    days == null ? "—" : `${days} day${days === 1 ? "" : "s"}`
  const fundingEnd = header.funding_end
  const stillDue = header.remaining_flight_funding - header.balance

  // MPN-1789. The window used to be min(schedule, billing cycle), which for a daily
  // rule was one day: the whole balance went to the networks as one day's cap. It is
  // now the active schedule days through the earlier of the flight end and the day the
  // funding rule stops paying, and the money paced over it counts deposits still to come.
  const fundingInfo = (
    <div style={{ textAlign: "left" }}>
      <div style={{ fontWeight: 600, marginBottom: "0.25rem" }}>
        Remaining Flight Funding = balance + deposits still due
      </div>
      <div>
        Balance today: <strong>{formatCurrency(header.balance)}</strong>
      </div>
      <div>
        Deposits due before the window ends: <strong>{formatCurrency(stillDue)}</strong>
      </div>
      <hr style={{ margin: "0.4rem 0", borderColor: "rgba(255,255,255,0.2)" }} />
      <div>
        This is what the campaigns below split, and what daily budgets pace over. On a
        subscription it is larger than the balance because deposits are still coming.
      </div>
    </div>
  )

  const daysRemainingInfo = (
    <div style={{ textAlign: "left" }}>
      <div style={{ fontWeight: 600, marginBottom: "0.25rem" }}>
        Days Remaining = active schedule days, through the funding end
      </div>
      <div>
        Active days left in schedule: <strong>{header.active_days_remaining}</strong>
      </div>
      <div>
        Funding rule ends: <strong>{fundingEnd ?? "open"}</strong>
      </div>
      <hr style={{ margin: "0.4rem 0", borderColor: "rgba(255,255,255,0.2)" }} />
      <div>
        Days paced over: <strong>{header.spend_window_days}</strong>
        {header.spend_window_days < header.active_days_remaining && (
          <> — the schedule runs past the funding rule; only the funded days are paced.</>
        )}
      </div>
    </div>
  )

  return (
    <div className="budget-header">
      {header.is_projected && (
        <OverlayTrigger
          placement="bottom"
          overlay={
            <Tooltip id="tt-projected" className="budget-tooltip">
              The budget hasn't started yet. These figures preview day 1 — the
              balance isn't spendable money yet.
            </Tooltip>
          }
        >
          <span className="budget-header__projected">Projected</span>
        </OverlayTrigger>
      )}
      <Stat label="Balance" value={formatCurrency(header.balance)} />
      <Stat
        label="Remaining Flight Funding"
        value={formatCurrency(header.remaining_flight_funding)}
        info={fundingInfo}
      />
      <Stat
        label="Days Remaining"
        value={`${header.spend_window_days}`}
        info={daysRemainingInfo}
      />
      <Stat label="Next Billing Cycle" value={cycleValue} />
      {/*
          Attributed network spend, the same figure the Monitor page shows.

          It used to read the debit ledger, which is written by a periodic command, so
          the two pages disagreed by however long that had been waiting to run. Nothing
          computed with this number -- it was only ever displayed -- so pointing it at
          the same source makes them agree by construction rather than by scheduling.
      */}
      <Stat
        label="Engagement Spend"
        value={formatCurrency(header.total_debits ?? 0)}
        info="Spend the ad networks have reported for this engagement. Matches Total Spend on the Monitor page."
      />
    </div>
  )
}

function Stat({
  label,
  value,
  info,
}: {
  label: string
  value: string
  info?: React.ReactNode
}) {
  return (
    <div className="budget-header__stat">
      <span className="budget-header__label">
        {label}
        {info && (
          <OverlayTrigger
            placement="bottom"
            overlay={
              <Tooltip
                id={`tt-${label.replace(/\s+/g, "-").toLowerCase()}`}
                className="budget-tooltip"
              >
                {info}
              </Tooltip>
            }
          >
            <i
              className="bi bi-info-circle ms-1"
              style={{ fontSize: "0.75rem", cursor: "help", opacity: 0.6 }}
            />
          </OverlayTrigger>
        )}
      </span>
      <span className="budget-header__value">{value}</span>
    </div>
  )
}
